import { useState, useCallback, useEffect } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Upload, X, Loader2, FileText, Maximize2, Tag, Check, Pencil, Edit3, Star } from "lucide-react";
import {
  Dialog, DialogContent,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import PhotoMarkupEditor from "./PhotoMarkupEditor";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const TAG_STYLES = {
  before: { bg: "bg-amber-100 text-amber-700 border-amber-200", label: "Before" },
  after: { bg: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "After" },
  general: { bg: "bg-slate-100 text-slate-500 border-slate-200", label: "General" },
};

export default function PhotoUpload({ entityType, entityId, photos = [], onPhotosChange, maxFiles = 20, showTags = false, defaultTag = "general", showCaptions = false, coverPhotoUrl = null, onSetCoverPhoto = null }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadTag, setUploadTag] = useState(defaultTag);
  const [captionDrafts, setCaptionDrafts] = useState({}); // id -> draft text
  const [savingCaption, setSavingCaption] = useState({});
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [markupTarget, setMarkupTarget] = useState(null);

  // Keep local draft state in sync when the photo list changes externally.
  useEffect(() => {
    setCaptionDrafts(prev => {
      const next = { ...prev };
      photos.forEach(p => {
        if (next[p.id] === undefined) next[p.id] = p.caption || "";
      });
      return next;
    });
  }, [photos]);

  const getFullUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const handleUpload = useCallback(async (files) => {
    if (!entityId) {
      toast.error("Save the record first, then upload photos");
      return;
    }
    if (files.length === 0) return;
    if (photos.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });

    // Upload everything in parallel so a 10-photo batch finishes in ~1 round-trip
    // instead of waiting for each request to complete sequentially. Each file
    // still POSTs to /api/uploads individually so progress + error reporting
    // stays granular.
    const tagParam = showTags && uploadTag && uploadTag !== "general" ? `&tag=${uploadTag}` : "";
    const tasks = files.map(async (file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return null;
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await axios.post(
          `${API_URL}/uploads?entity_type=${entityType}&entity_id=${entityId}${tagParam}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return res.data;
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        return null;
      } finally {
        setUploadProgress(p => ({ done: p.done + 1, total: p.total }));
      }
    });

    const settled = await Promise.all(tasks);
    const newPhotos = settled.filter(Boolean);

    if (newPhotos.length > 0) {
      onPhotosChange([...photos, ...newPhotos]);
      toast.success(`${newPhotos.length} file${newPhotos.length > 1 ? "s" : ""} uploaded`);
    }
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
  }, [entityType, entityId, photos, onPhotosChange, maxFiles, showTags, uploadTag]);

  const handleDelete = async (fileId) => {
    try {
      await axios.delete(`${API_URL}/uploads/${fileId}`);
      onPhotosChange(photos.filter(p => p.id !== fileId));
      toast.success("File deleted");
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handleTagChange = async (fileId, newTag) => {
    try {
      await axios.patch(`${API_URL}/uploads/${fileId}/tag?tag=${newTag}`);
      onPhotosChange(photos.map(p => p.id === fileId ? { ...p, tag: newTag } : p));
    } catch (error) {
      toast.error("Failed to update tag");
    }
  };

  const saveCaption = async (fileId) => {
    const text = captionDrafts[fileId] ?? "";
    setSavingCaption(s => ({ ...s, [fileId]: true }));
    try {
      await axios.patch(`${API_URL}/uploads/${fileId}/caption`, null, { params: { caption: text } });
      onPhotosChange(photos.map(p => p.id === fileId ? { ...p, caption: text } : p));
      toast.success("Caption saved");
    } catch (error) {
      toast.error("Failed to save caption");
    } finally {
      setSavingCaption(s => ({ ...s, [fileId]: false }));
    }
  };

  const startRename = (photo) => {
    setRenamingId(photo.id);
    // Strip the extension so the user only edits the meaningful part
    const name = photo.original_name || "";
    const dot = name.lastIndexOf(".");
    setRenameDraft(dot > 0 ? name.substring(0, dot) : name);
  };

  const commitRename = async (fileId) => {
    const next = (renameDraft || "").trim();
    if (!next) {
      setRenamingId(null);
      return;
    }
    try {
      const res = await axios.patch(`${API_URL}/uploads/${fileId}/rename`, { original_name: next });
      onPhotosChange(photos.map(p => p.id === fileId ? { ...p, original_name: res.data.original_name } : p));
      toast.success("Renamed");
    } catch (error) {
      toast.error("Could not rename file");
    } finally {
      setRenamingId(null);
    }
  };

  const handleAnnotated = (updated) => {
    if (!updated) return;
    // Cache-bust the served URL so the new annotated bytes show immediately
    const bust = `${updated.url}${updated.url.includes('?') ? '&' : '?'}v=${Date.now()}`;
    onPhotosChange(photos.map(p => p.id === updated.id ? { ...p, ...updated, url: bust } : p));
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleUpload(files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    handleUpload(files);
  };

  const isImage = (file) => {
    const ext = (file.original_name || file.filename || "").toLowerCase();
    return ext.endsWith(".jpg") || ext.endsWith(".jpeg") || ext.endsWith(".png") || ext.endsWith(".gif") || ext.endsWith(".webp");
  };

  return (
    <div className="space-y-3">
      {/* Tag selector + Drop Zone */}
      {showTags && (
        <div className="flex items-center gap-2 mb-2">
          <Tag className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">Upload as:</span>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {["before", "after", "general"].map(tag => (
              <button
                key={tag}
                type="button"
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${uploadTag === tag ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                onClick={() => setUploadTag(tag)}
                data-testid={`tag-select-${tag}`}
              >
                {tag === "before" ? "Before" : tag === "after" ? "After" : "General"}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className={`border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-colors cursor-pointer ${dragActive ? "border-[#0F4C81] bg-[#0F4C81]/5" : "border-slate-300 hover:border-slate-400"}`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById(`file-input-${entityType}-${entityId}`)?.click()}
        data-testid="photo-drop-zone"
      >
        <input
          id={`file-input-${entityType}-${entityId}`}
          type="file"
          accept="image/*,.pdf"
          multiple
          className="hidden"
          onChange={handleFileInput}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
            <p className="text-sm text-slate-500">
              Uploading {uploadProgress.done} / {uploadProgress.total}…
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-slate-400" />
            <p className="text-sm text-slate-600 font-medium">Drop files or tap to upload</p>
            <p className="text-xs text-slate-400">Select many at once · JPG, PNG, GIF, WEBP, PDF (max 10MB each)</p>
          </div>
        )}
      </div>

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo) => {
            const tagStyle = TAG_STYLES[photo.tag || "general"] || TAG_STYLES["general"];
            // A photo is the current "cover" if either its stored url
            // (relative) or its expanded absolute url matches the project's
            // cover_photo_url. `?v=` cache-busters get stripped before compare.
            const stripBust = (u) => (u || "").split("?")[0];
            const isCover = !!(coverPhotoUrl && (
              stripBust(coverPhotoUrl) === stripBust(photo.url) ||
              stripBust(coverPhotoUrl) === stripBust(getFullUrl(photo.url))
            ));
            return (
              <div
                key={photo.id}
                className={`group relative rounded-lg overflow-hidden border bg-slate-50 cursor-pointer ${isCover ? "border-amber-400 ring-2 ring-amber-300" : "border-slate-200"}`}
                onClick={() => {
                  if (isImage(photo)) setPreviewUrl(getFullUrl(photo.url));
                  else window.open(getFullUrl(photo.url), "_blank", "noopener,noreferrer");
                }}
                title={isImage(photo) ? "Click to preview" : "Click to open file"}
                data-testid={`photo-${photo.id}`}
              >
                {isImage(photo) ? (
                  <img src={getFullUrl(photo.url)} alt={photo.original_name} className="w-full h-28 object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-28 flex flex-col items-center justify-center bg-slate-100 gap-1">
                    <FileText className="w-8 h-8 text-slate-400" />
                    <span className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">
                      {(photo.original_name || photo.filename || "file").split(".").pop().slice(0, 4)}
                    </span>
                  </div>
                )}
                {isCover && (
                  <div className="absolute top-1.5 left-1.5 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-900 text-[10px] font-bold shadow-sm" data-testid={`photo-cover-badge-${photo.id}`}>
                    <Star className="w-3 h-3 fill-amber-900" />Cover
                  </div>
                )}
                <div className="p-2">
                  {renamingId === photo.id ? (
                    <input
                      autoFocus
                      type="text"
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onBlur={() => commitRename(photo.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); commitRename(photo.id); }
                        if (e.key === "Escape") { e.preventDefault(); setRenamingId(null); }
                      }}
                      className="w-full text-xs px-1.5 py-1 rounded border border-[#0F4C81] focus:outline-none"
                      data-testid={`photo-rename-input-${photo.id}`}
                    />
                  ) : (
                    <button
                      type="button"
                      title="Click to rename"
                      onClick={(e) => { e.stopPropagation(); startRename(photo); }}
                      className="block w-full text-left text-xs text-slate-600 truncate hover:text-[#0F4C81] hover:underline"
                      data-testid={`photo-name-${photo.id}`}
                    >
                      {photo.original_name}
                    </button>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-[10px] text-slate-400">{(photo.size / 1024).toFixed(0)} KB</p>
                    {showTags && <Badge className={`text-[10px] ${tagStyle.bg}`}>{tagStyle.label}</Badge>}
                  </div>
                  {showCaptions && (
                    <div className="mt-1.5 flex items-center gap-1">
                      <input
                        type="text"
                        value={captionDrafts[photo.id] ?? (photo.caption || "")}
                        onChange={(e) => setCaptionDrafts(d => ({ ...d, [photo.id]: e.target.value }))}
                        onBlur={() => {
                          const current = captionDrafts[photo.id] ?? "";
                          if (current !== (photo.caption || "")) saveCaption(photo.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                        }}
                        placeholder="Add a caption…"
                        maxLength={240}
                        className="flex-1 min-w-0 text-[11px] px-1.5 py-1 rounded border border-slate-200 focus:border-[#0F4C81] focus:outline-none"
                        data-testid={`photo-caption-${photo.id}`}
                      />
                      {savingCaption[photo.id] ? (
                        <Loader2 className="w-3 h-3 animate-spin text-slate-400 flex-shrink-0" />
                      ) : (captionDrafts[photo.id] ?? "") !== (photo.caption || "") ? (
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => saveCaption(photo.id)}
                          className="text-emerald-600 hover:text-emerald-700 flex-shrink-0"
                          title="Save caption"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {isImage(photo) ? (
                    <Button type="button" variant="ghost" size="sm" className="text-white hover:bg-white/20 h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); setPreviewUrl(getFullUrl(photo.url)); }} title="Preview">
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 h-8 w-8 p-0"
                      onClick={(e) => { e.stopPropagation(); window.open(getFullUrl(photo.url), "_blank", "noopener,noreferrer"); }}
                      title="Open file in new tab"
                      data-testid={`photo-open-${photo.id}`}
                    >
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                  )}
                  {isImage(photo) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 h-8 w-8 p-0"
                      onClick={(e) => { e.stopPropagation(); setMarkupTarget(photo); }}
                      title="Annotate / mark up"
                      data-testid={`photo-edit-${photo.id}`}
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                  )}
                  {isImage(photo) && onSetCoverPhoto && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={`h-8 w-8 p-0 ${isCover ? "text-amber-300 hover:bg-amber-400/30" : "text-white hover:bg-amber-400/30"}`}
                      onClick={(e) => { e.stopPropagation(); onSetCoverPhoto(isCover ? null : photo); }}
                      title={isCover ? "Remove as cover photo" : "Set as cover photo (used on Job Complete + Review Request MMS)"}
                      data-testid={`photo-set-cover-${photo.id}`}
                    >
                      <Star className={`w-4 h-4 ${isCover ? "fill-amber-300" : ""}`} />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20 h-8 w-8 p-0"
                    onClick={(e) => { e.stopPropagation(); startRename(photo); }}
                    title="Rename"
                    data-testid={`photo-rename-${photo.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {showTags && (
                    <Select value={photo.tag || "general"} onValueChange={(v) => handleTagChange(photo.id, v === "general" ? "" : v)}>
                      <SelectTrigger className="h-8 w-20 bg-white/20 border-0 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="before">Before</SelectItem>
                        <SelectItem value="after">After</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Button type="button" variant="ghost" size="sm" className="text-white hover:bg-red-500/50 h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }} data-testid={`photo-delete-${photo.id}`} title="Delete">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-3xl p-2">
          {previewUrl && <img src={previewUrl} alt="Preview" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>

      {/* Markup / Annotate Editor */}
      <PhotoMarkupEditor
        open={!!markupTarget}
        file={markupTarget}
        onClose={() => setMarkupTarget(null)}
        onSaved={handleAnnotated}
      />
    </div>
  );
}

// Before/After Comparison Component
export function BeforeAfterComparison({ photos = [] }) {
  const [selectedBefore, setSelectedBefore] = useState(null);
  const [selectedAfter, setSelectedAfter] = useState(null);

  const getFullUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const beforePhotos = photos.filter(p => p.tag === "before");
  const afterPhotos = photos.filter(p => p.tag === "after");

  if (beforePhotos.length === 0 && afterPhotos.length === 0) {
    return (
      <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
        <p className="text-sm text-slate-400">No before/after photos tagged yet</p>
        <p className="text-xs text-slate-400 mt-1">Upload photos and tag them as "Before" or "After"</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Side-by-side comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-2">
            <span className="w-3 h-3 bg-amber-400 rounded-full" />Before
          </h4>
          {selectedBefore ? (
            <div className="relative rounded-lg overflow-hidden border-2 border-amber-300">
              <img src={getFullUrl(selectedBefore.url)} alt="Before" className="w-full h-48 sm:h-64 object-cover" />
              <button type="button" className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1" onClick={() => setSelectedBefore(null)}>
                <X className="w-4 h-4" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-amber-500/80 text-white text-xs py-1 px-2 text-center">{selectedBefore.original_name}</div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {beforePhotos.length > 0 ? beforePhotos.map(p => (
                <img
                  key={p.id}
                  src={getFullUrl(p.url)}
                  alt={p.original_name}
                  className="w-full h-20 object-cover rounded-lg cursor-pointer border-2 border-transparent hover:border-amber-400 transition-colors"
                  onClick={() => setSelectedBefore(p)}
                  data-testid={`compare-before-${p.id}`}
                />
              )) : (
                <div className="col-span-3 h-20 flex items-center justify-center bg-slate-50 rounded-lg text-xs text-slate-400">No before photos</div>
              )}
            </div>
          )}
        </div>

        <div>
          <h4 className="text-sm font-medium text-emerald-700 mb-2 flex items-center gap-2">
            <span className="w-3 h-3 bg-emerald-400 rounded-full" />After
          </h4>
          {selectedAfter ? (
            <div className="relative rounded-lg overflow-hidden border-2 border-emerald-300">
              <img src={getFullUrl(selectedAfter.url)} alt="After" className="w-full h-48 sm:h-64 object-cover" />
              <button type="button" className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1" onClick={() => setSelectedAfter(null)}>
                <X className="w-4 h-4" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-emerald-500/80 text-white text-xs py-1 px-2 text-center">{selectedAfter.original_name}</div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {afterPhotos.length > 0 ? afterPhotos.map(p => (
                <img
                  key={p.id}
                  src={getFullUrl(p.url)}
                  alt={p.original_name}
                  className="w-full h-20 object-cover rounded-lg cursor-pointer border-2 border-transparent hover:border-emerald-400 transition-colors"
                  onClick={() => setSelectedAfter(p)}
                  data-testid={`compare-after-${p.id}`}
                />
              )) : (
                <div className="col-span-3 h-20 flex items-center justify-center bg-slate-50 rounded-lg text-xs text-slate-400">No after photos</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="flex gap-4 text-xs text-slate-500">
        <span>{beforePhotos.length} before photo{beforePhotos.length !== 1 ? "s" : ""}</span>
        <span>{afterPhotos.length} after photo{afterPhotos.length !== 1 ? "s" : ""}</span>
      </div>
    </div>
  );
}
